import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { QuestionType } from '@/constants/QuestionType';
import { hasLocalizedText } from '@/utils/localization';

// Competencies expose four question types. They map onto the existing
// QuestionType constants used across the questionnaire module and feed the
// shared QuestionForm's `allowedTypes` prop.
export const competencyQuestionTypes = [
    QuestionType.RATING_SCALE,
    QuestionType.MULTI_CHOICE,
    QuestionType.EMOJI_CHOICE,
    QuestionType.YES_NO,
] as const;

export const createEmptyQuestion = (
    competencyId: string,
    sortOrder: number,
    language: string,
): ReviewQuestion => ({
    id: crypto.randomUUID(),
    competencyId,
    questionType: QuestionType.MULTI_CHOICE,
    label: { [language]: '' },
    usefulTip: {},
    sortOrder,
    // The competency form hides these controls, so the defaults decide: a competency question can always be
    // skipped and always accepts a note (bug #15654).
    isAnswerMandatory: false,
    isCommentAllowed: true,
    isIncludedInBestOrWorstQuestions: false,
    isIncludedInScoreCalculation: false,
    hasOtherOption: false,
    status: ReviewQuestionStatus.ACTIVE,
    reviewQuestionOptions: [],
});

export const isQuestionActive = (question: ReviewQuestion): boolean =>
    question.status === ReviewQuestionStatus.ACTIVE;

const stripEmptyValues = (dict?: Record<string, string>): Record<string, string> =>
    Object.fromEntries(
        Object.entries(dict ?? {}).filter(
            ([, value]) => typeof value === 'string' && value.trim() !== '',
        ),
    );

export const hasMeaningfulLabel = (label?: Record<string, string>): boolean =>
    hasLocalizedText(label);

// The backend rejects any label dictionary that contains empty string values.
// Option labels (rating-scale bounds, emoji captions) are optional in the UI, so
// we strip empty values before saving and drop questions that have no label at all.
export const sanitizeQuestionsForSave = (questions: ReviewQuestion[]): ReviewQuestion[] =>
    questions
        .filter((question) => hasMeaningfulLabel(question.label))
        .map((question, index) => ({
            ...question,
            sortOrder: index,
            label: stripEmptyValues(question.label),
            usefulTip: stripEmptyValues(question.usefulTip),
            // Every option sent must carry a non-empty label (backend rule), so we
            // drop options whose label is blank (e.g. unlabeled rating-scale bounds).
            reviewQuestionOptions: (question.reviewQuestionOptions ?? [])
                .map((option) => ({ ...option, label: stripEmptyValues(option.label) }))
                .filter((option) => Object.keys(option.label).length > 0)
                .map((option, optionIndex) => ({ ...option, sortOrder: optionIndex })),
        }));
